import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { StepComponent } from '../../step.component';
import { PlanStep } from '../../../services/PlanStep';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { PlanStepPage } from '../../../services/PlanStepPage';
import { ResourceService } from '../../../services/resource.service';
import { IResource } from '../../../services/IResource';
import { Observable } from 'rxjs/Observable';
import {BehaviorSubject, Subscription} from 'rxjs/index';
import {StrategiesDataStoreService} from '../../../../shared/services/strategies-data-store.service';
import {take} from 'rxjs/internal/operators';
import {IPriorityDropdownOptionValue, PriorityDropdownOption} from '../../../../shared/models/IPriorityDropdownOption';
import {StrtPriority} from '../../../../shared/models/plan/StrtPriority';
import {StrtInitiative} from '../../../../shared/models/plan/StrtInitiative';
import {StrtObjective} from '../../../../shared/models/plan/StrtObjective';
import {PriorityDropdownComponent} from '../../../../shared/components/priority-dropdown/priority-dropdown.component';
import {PriorityService} from '../../../services/priority.service';
import {DataUtils} from '../../../../shared/utilities/data-utils';
import {AnalysisExpansion} from '../../../../shared/models/ui/AnalysisExpansion';
import {AppLoadingService} from '../../../../app-loading-panel/app-loading.service';
import {InitiativeService} from '../../../services/initiative.service';
import {DomSanitizer} from '@angular/platform-browser';


@Component({
  selector: 'app-outcome-impact',
  templateUrl: './outcome-impact.component.html',
  styleUrls: ['./outcome-impact.component.scss']
})
export class OutcomeImpactComponent extends StepComponent<IResource> implements OnInit, OnDestroy {

  @Input() strategyId: number;
  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();
  @ViewChild(PriorityDropdownComponent) priorityDropdown: PriorityDropdownComponent;

  private _subscriptions: Subscription[] = [];

  initiativePriorities: StrtPriority[] = [];
  filteredInitiativePriorities: StrtPriority[] = [];
  filteredInitiatives: StrtInitiative[] = [];
  selectOptions: PriorityDropdownOption[] = [];
  selectedOptions: PriorityDropdownOption[] = [];
  isLoading$: Observable<boolean>;
  getPrioritiesCalled = false;
  priorities: StrtPriority[];
  planHasInitiatives = false;
  planHasPriorities = false;
  analysisDeleteActive = false;
  initiativeToDeleteAnalysis?: StrtInitiative;

  private _expandedLocationBS: BehaviorSubject<AnalysisExpansion> = new BehaviorSubject<AnalysisExpansion|null>(null);
  expandedLocation$ = this._expandedLocationBS.asObservable();
  private _lastKnownExpandedLocation: AnalysisExpansion;

  constructor(
      _resourceService: ResourceService,
      private _strtDataStore: StrategiesDataStoreService,
      private _priorityService: PriorityService,
      private _domSanitizer: DomSanitizer,
      private _initiativeService: InitiativeService,
      private _loadingService: AppLoadingService) {
    super(PlanCoordinate.stepBy(PlanStep.DETERMINE_IMPACT_OF_CRITICAL_INITIATIVES), _resourceService);
  }

  ngOnInit() {
    this.getPrioritiesCalled = false;
    this.isLoading$ = this._loadingService.isLoading$;
    this.expandedLocation$.subscribe((analysisExpansion: AnalysisExpansion) => {
      this._lastKnownExpandedLocation = analysisExpansion;
    });
    const prioritySub = this._strtDataStore.priorities$.subscribe(this.prioritiesHandler.bind(this));
    this._subscriptions.push(prioritySub);
    this._priorityService.fetchPriorities(this.strategyId);
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
    this._subscriptions = [];
  }

  onDropdownSelect(event) {
    this.selectedOptions = event;
  }

  prioritiesHandler(newPriorities: StrtPriority[]) {
    this.priorities = newPriorities;
    this.initiativePriorities = newPriorities.filter(
      priority => {
        const objectivesWithInitiatives = priority.objectives.filter(objective => objective.initiatives.length > 0);
        return objectivesWithInitiatives.length > 0;
      }
    );
    this.planHasPriorities = this.priorities && this.priorities.length > 0;
    this.planHasInitiatives = this.initiativePriorities && this.initiativePriorities.length > 0;
    this.selectOptions = PriorityDropdownOption.fromPriorityInitiatives(this.initiativePriorities);
    if (this.getPrioritiesCalled && this.selectOptions && this.selectOptions.length > 1) {
      this.filteredInitiativePriorities = this.initiativePriorities;
      setTimeout(() => {
        this.priorityDropdown.selectAll();
        this.displaySelectedInitiatives();
      }, 100);
    } else if (this.getPrioritiesCalled && this.selectOptions && this.selectOptions.length <= 1) {
      setTimeout(() => {
        this.priorityDropdown.selectAll();
      }, 100);
    }
    if (this.getPrioritiesCalled) {
      this._loadingService.clearLoading();
    }
    this.getPrioritiesCalled = true;
  }

  displaySelectedInitiatives() {
    // fetch latest selections from dropdown component.
    const selectedItems: IPriorityDropdownOptionValue[] = this.priorityDropdown.selectedOptions;

    if (selectedItems.length === 1 && selectedItems[0].primaryId === -1) {
      const nestedInitiatives = this.initiativePriorities.map((priority: StrtPriority) => {
        return priority.objectives.map((objective: StrtObjective) => {
          return objective.initiatives;
        });
      });
      this.filteredInitiatives = DataUtils.flatten(nestedInitiatives);
    } else {
      this.filteredInitiatives = [];
      const initiativeIds: number[] = selectedItems.map((item: IPriorityDropdownOptionValue) => {
        return item.primaryId;
      });

      const nestedInitiatives = this.initiativePriorities.map((priority: StrtPriority) => {
        return priority.objectives.map((objective: StrtObjective) => {
          return objective.initiatives.filter((initiative: StrtInitiative) => {
            return initiativeIds.indexOf(initiative.initiativeId) >= 0;
          });
        });
      });

      this.filteredInitiatives = DataUtils.flatten(nestedInitiatives);
    }
  }

  onClickViewTheoryOfAction(initiative: StrtInitiative) {
    this._strtDataStore.toaAutoSelectInitiative = initiative;
    this.planCoordinateChange.emit(PlanCoordinate.stepPageBy(PlanStepPage.TheoryOfAction));
  }

  showAnalysisInput(initiative: StrtInitiative, isEdit: boolean = false) {
    this._expandedLocationBS.next({
      analysis: initiative.analysis,
      isEdit: isEdit,
      isCreate: !isEdit,
      isDelete: false,
      initiative: initiative.initiativeId,
      objective: initiative.objectiveId
    });
  }

  showAnalysis(initiative: StrtInitiative) {
    this._expandedLocationBS.next({
      analysis: initiative.analysis,
      isEdit: false,
      isCreate: false,
      isDelete: false,
      initiative: initiative.initiativeId,
      objective: initiative.objectiveId
    });
  }

  resetExpandedInitiative() {
    if (this._lastKnownExpandedLocation.isEdit) {
      this._expandedLocationBS.next({...this._lastKnownExpandedLocation, isEdit: false});
    } else {
      this._expandedLocationBS.next(null);
    }
  }

  /**
   * An analysis must exist already before you can get here
   * @param {StrtInitiative} initiative
   */
  toggleInitiativeExpansion(initiative: StrtInitiative) {
    // If nothing was expanded before, expand this one
    if (!this._lastKnownExpandedLocation) {
      this.showAnalysis(initiative);
    } else {
      this._expandedLocationBS.next(null);
    }
  }

  onAnalysisSave() {
    this.expandedLocation$.pipe(take(1)).subscribe((analysisExpansion: AnalysisExpansion) => {
      let observable: Observable<any>;
      if (analysisExpansion.isEdit) {
        observable = this._initiativeService.editInitiativeAnalysis(this.strategyId,
          analysisExpansion.objective,
          analysisExpansion.initiative,
          analysisExpansion.analysis);
      } else {
        observable = this._initiativeService.createInitiativeAnalysis(this.strategyId,
          analysisExpansion.objective,
          analysisExpansion.initiative,
          analysisExpansion.analysis);
      }
      if (observable) {
        this._loadingService.setLoading();
        observable.pipe(take(1))
          .subscribe((result) => {
            const initiative: StrtInitiative = DataUtils.create(this.priorities)
              .getInitiativePathById(analysisExpansion.initiative).initiative;
            initiative.analysis = result;
            this._strtDataStore.updateInitiative(initiative, this.priorities);
            this.resetExpandedInitiative();
          }, (err) => {
            console.error(`On Citation Save/Edit Err: `, err);
            this._loadingService.clearLoading();
          });
      }

    });
  }

  onAnalysisChanged(data: any) {
    const updatedAnalysis: AnalysisExpansion = JSON.parse(JSON.stringify(this._lastKnownExpandedLocation));
    if (!updatedAnalysis.analysis) {
      updatedAnalysis.analysis = {
        analysisId: null,
        text: data ? (data.trim() === '' ? null : data) : null,
        lastUpdatedUserLastName: null,
        lastUpdatedUserFirstName: null,
        lastUpdatedDate: null,
        strategyId: this.strategyId,
        sort: 0,
        initiativeId: updatedAnalysis.initiative
      };
    } else {
      updatedAnalysis.analysis.text = data ? (data.trim() === '' ? null : data) : null;
    }
    setTimeout(() => {
      this._expandedLocationBS.next(updatedAnalysis);
    });
  }

  doesExpandedLocationMatch(initiativeId: number) {
    return this._lastKnownExpandedLocation && this._lastKnownExpandedLocation.initiative === initiativeId;
  }

  safeText(text: string) {
    return this._domSanitizer.bypassSecurityTrustHtml(text);
  }

  deleteAnalysis(initiative: StrtInitiative) {
    this.initiativeToDeleteAnalysis = initiative;
    this.analysisDeleteActive = true;
  }

  onAnalysisDelete() {
    this._loadingService.setLoading();
    this._initiativeService.deleteInitiativeAnalysis(this.strategyId,
                                                    this.initiativeToDeleteAnalysis.objectiveId,
                                                    this.initiativeToDeleteAnalysis.initiativeId,
                                                    this.initiativeToDeleteAnalysis.analysis.analysisId)
      .pipe(take(1)).subscribe((result) => {
      this.initiativeToDeleteAnalysis.analysis = null;
      this._strtDataStore.updateInitiative(this.initiativeToDeleteAnalysis, this.priorities);
      this.resetExpandedInitiative();
      this.resetAnalysisDelete();
    }, (err) => {
      console.error(`On Analysis Delete Error: `, err);
      this._loadingService.clearLoading();
      this.resetAnalysisDelete();
    });
  }

  resetAnalysisDelete() {
    this.initiativeToDeleteAnalysis = null;
    this.analysisDeleteActive = false;
  }

}
